import React, { useEffect, useState } from 'react';
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, RefreshCw } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import api from '../../api/api';

export default function ContactMessages() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchMessages = async (showRefresh = false) => {
        if (showRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);
        try {
            const res = await api.get('/contact/admin/messages');
            setMessages(res.data.messages || []);
        } catch (err) {
            setError('Failed to fetch contact messages');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { fetchMessages(); }, []);

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900'}`}>
            <div className="max-w-6xl mx-auto px-4 py-8">

                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'}`}
                            title="Back to Dashboard"
                        >
                            <ArrowLeft size={22} />
                        </button>
                        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-primary-600 to-teal-600' : 'bg-gradient-to-br from-primary-500 to-teal-500'}`}>
                            <MessageSquare size={24} className="text-white" />
                        </div>
                        <div>
                            <h1 className={`text-2xl font-extrabold tracking-tight ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Contact Messages</h1>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {messages.length.toLocaleString()} submission{messages.length === 1 ? '' : 's'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => fetchMessages(true)}
                        disabled={refreshing}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200'}`}
                    >
                        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                {/* Content */}
                {loading ? (
                    <InlineTableSkeleton isDarkMode={isDarkMode} rows={8} cols={5} />
                ) : error ? (
                    <div className={`text-center py-16 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <p className="text-red-500 font-semibold text-lg mb-2">Something went wrong</p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{error}</p>
                        <button onClick={() => fetchMessages()} className="mt-4 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors">
                            Try Again
                        </button>
                    </div>
                ) : messages.length === 0 ? (
                    <div className={`text-center py-20 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <MessageSquare size={48} className={`mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                        <p className={`text-lg font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>No messages yet</p>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Contact Us submissions will show up here.
                        </p>
                    </div>
                ) : (
                    <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className={isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-50 text-slate-500'}>
                                        <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Submitted</th>
                                        <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Name</th>
                                        <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Email</th>
                                        <th className="text-left font-semibold px-4 py-3">Message</th>
                                        <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Account</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {messages.map((msg) => (
                                        <tr
                                            key={msg.ContactMessageID}
                                            className={`border-t align-top ${isDarkMode ? 'border-slate-700 hover:bg-slate-800/60' : 'border-slate-100 hover:bg-slate-50'}`}
                                        >
                                            <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                {msg.CreatedOn}
                                            </td>
                                            <td className={`px-4 py-3 font-semibold whitespace-nowrap ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                {msg.Name}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <a
                                                    href={`mailto:${msg.Email}`}
                                                    className={isDarkMode ? 'text-primary-400 hover:underline' : 'text-primary-600 hover:underline'}
                                                >
                                                    {msg.Email}
                                                </a>
                                            </td>
                                            <td className={`px-4 py-3 max-w-md ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {msg.Message}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {msg.ClientID ? (
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${isDarkMode ? 'bg-primary-900/40 text-primary-300 border-primary-700' : 'bg-primary-50 text-primary-700 border-primary-200'}`}>
                                                        Logged in
                                                    </span>
                                                ) : (
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${isDarkMode ? 'bg-slate-700 text-slate-400 border-slate-600' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                                                        Guest
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
